/**
 * 엑셀 내보내기
 * ---------------------------------------------------------------------------
 * ## 왜 CSV 인가
 * xlsx 를 만들려면 라이브러리가 필요하고(압축된 XML 묶음이다) 번들이 수백
 * KB 늘어난다. 내보내는 자료는 숫자와 날짜가 든 표 하나뿐이라, 엑셀이
 * 그대로 여는 CSV 로 충분하다.
 *
 * ## BOM 을 반드시 붙인다
 * 엑셀은 BOM 이 없는 UTF-8 CSV 를 **시스템 기본 인코딩(한국어 윈도는 CP949)**
 * 으로 읽는다. 그래서 한글이 전부 깨진다. 앞에 `﻿` 를 한 글자 붙이면
 * 엑셀이 UTF-8 로 알아본다 — 이것 하나로 갈린다.
 *
 * ## 숫자를 문자열로 감싸지 않는다
 * 금액을 `"1,234"` 로 내보내면 엑셀이 글자로 읽어 합계가 되지 않는다.
 * 숫자는 쉼표 없이 그대로 쓴다. 보기 좋게 만드는 것은 받는 쪽의 서식이다.
 */

export interface CsvColumn<T> {
  header: string;
  /** 값. 숫자를 돌려주면 엑셀에서 숫자로 들어간다 */
  value: (row: T) => string | number | null | undefined;
}

/**
 * 한 칸을 CSV 규칙에 맞게 감싼다.
 *
 * 쉼표·따옴표·줄바꿈이 들어 있으면 따옴표로 감싸고, 안의 따옴표는 두 번
 * 적는다. 상호에 쉼표가 들어가는 일은 흔하다((주)가나, 다라).
 */
function cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';

  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv<T>(rows: T[], columns: Array<CsvColumn<T>>): string {
  const head = columns.map((c) => cell(c.header)).join(',');
  const body = rows.map((r) => columns.map((c) => cell(c.value(r))).join(','));
  return [head, ...body].join('\r\n');
}

/**
 * 내려받게 한다.
 *
 * 파일명에 날짜를 넣는다. 「청구서.csv」가 내려받기 폴더에 여러 개 쌓이면
 * 어느 것이 무엇인지 알 수 없다.
 */
export function downloadCsv<T>(
  fileName: string,
  rows: T[],
  columns: Array<CsvColumn<T>>,
): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const csv = `﻿${toCsv(rows, columns)}`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}_${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // 바로 지우면 사파리에서 내려받기가 끊긴다. 한 틱 뒤에 치운다.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 날짜 칸. 엑셀이 날짜로 알아보도록 YYYY-MM-DD 로 적는다 */
export function csvDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}
